import {
  CallbackContext,
  JwtSigner,
  jwtSignerFromJwt,
  verifyJwt,
} from "@openid4vc/oauth2";
import {
  CONTENT_TYPES,
  HEADERS,
  UnexpectedStatusCodeError,
  ValidationError,
  createFetcher,
  hasStatusOrThrow,
} from "@pagopa/io-wallet-utils";

import { decodeJwt } from "../common/jwt/decode-jwt";
import { MrtdPopError } from "../errors";
import {
  zMrtdPopInitResponseJwtHeader,
  zMrtdPopInitResponseJwtPayload,
} from "./z-mrtd-pop";

export interface FetchMrtdPopInitOptions {
  callbacks: Pick<CallbackContext, "fetch" | "verifyJwt">;
  clientAttestationDPoP: string;
  /** From challenge JWT payload */
  mrtdAuthSession: string;
  /** From challenge JWT payload */
  mrtdPopJwtNonce: string;
  /** From challenge JWT payload htu */
  popInitEndpoint: string;

  /**
   * Optional custom signer for verifying the MRTD PoP init response JWT.
   * If not provided, the library will attempt to verify using JWT header.
   */
  signer?: JwtSigner;

  /**
   * Attestation header value for the wallet's own attestation, to be included in the request.
   */
  walletAttestation: string;
}

export interface FetchMrtdPopInitResult {
  challenge: string;
  mrtdPopNonce: string;
  mrz?: string;
  /** htu from the init response — the verify endpoint */
  popVerifyEndpoint: string;
  signer: JwtSigner;
}

/**
 * Initiates MRTD Proof of Possession validation (Phase 3.2 of L2+ flow).
 *
 * Sends session correlation parameters to the MRTD PoP Service and receives:
 * - Cryptographic challenge for Anti-Cloning Internal Authentication
 * - Nonce for next step
 * - Optional MRZ data from CIE National Registry
 *
 * @param options - Session parameters, attestation headers, and callbacks
 * @returns Challenge, nonce, optional MRZ, and verify endpoint URL
 * @throws {UnexpectedStatusCodeError} If response is not HTTP 202
 * @throws {MrtdPopError} For network or parsing failures
 *
 * It is alligned to the IT-Wallet v1.3 specs
 * @see IT-Wallet L2+ specification Section 12.1.3.5.3.2-3 (MRTD PoP Request/Response)
 */
export async function fetchMrtdPopInit(
  options: FetchMrtdPopInitOptions,
): Promise<FetchMrtdPopInitResult> {
  try {
    const fetch = createFetcher(options.callbacks.fetch);

    const response = await fetch(options.popInitEndpoint, {
      body: JSON.stringify({
        mrtd_auth_session: options.mrtdAuthSession,
        mrtd_pop_jwt_nonce: options.mrtdPopJwtNonce,
      }),
      headers: {
        [HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
        [HEADERS.OAUTH_CLIENT_ATTESTATION]: options.walletAttestation,
        [HEADERS.OAUTH_CLIENT_ATTESTATION_POP]: options.clientAttestationDPoP,
      },
      method: "POST",
    });

    await hasStatusOrThrow(202, UnexpectedStatusCodeError)(response);

    const responseJwt = await response.text();

    const jwt = decodeJwt({
      errorMessagePrefix: "Error decoding MRTD PoP init response JWT:",
      headerSchema: zMrtdPopInitResponseJwtHeader,
      jwt: responseJwt,
      payloadSchema: zMrtdPopInitResponseJwtPayload,
    });

    const { signer } = await verifyJwt({
      compact: responseJwt,
      errorMessage: "Error verifying MRTD PoP init response JWT",
      header: jwt.header,
      payload: jwt.payload,
      signer:
        options.signer ??
        jwtSignerFromJwt({ header: jwt.header, payload: jwt.payload }),
      verifyJwtCallback: options.callbacks.verifyJwt,
    });

    return {
      challenge: jwt.payload.challenge,
      mrtdPopNonce: jwt.payload.mrtd_pop_nonce,
      mrz: jwt.payload.mrz,
      popVerifyEndpoint: jwt.payload.htu,
      signer,
    };
  } catch (error) {
    if (
      error instanceof UnexpectedStatusCodeError ||
      error instanceof ValidationError
    ) {
      throw error;
    }
    throw new MrtdPopError(
      `Unexpected error during MRTD PoP init: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}
